import * as z from 'zod';

import type { WelcomeBonusScope, WelcomeBonusStatus } from '../@types/api';
import {
  AFFILIATE_BONUS_CATEGORY_OPTIONS,
  WELCOME_BONUS_ELIGIBILITY_OPTIONS,
  WELCOME_BONUS_PRODUCT_TYPE_OPTIONS,
  WELCOME_BONUS_REWARD_TYPE_OPTIONS,
  WELCOME_BONUS_STATUS_OPTIONS,
  WELCOME_BONUS_SCOPE_OPTIONS,
  WELCOME_BONUS_TYPE_OPTIONS,
  WELCOME_BONUS_VALIDITY_PERIOD_OPTIONS,
  WELCOME_BONUS_WAGERING_MODE_OPTIONS,
  isValidWelcomeBonusStatusTransition,
} from '../constants';
import {
  AFFILIATE_BONUS_CATEGORY,
  AFFILIATE_CAMPAIGN_PRODUCT,
  CAMPAIGN_REWARD_TYPE,
  WELCOME_BONUS_WAGERING_MODE,
  WELCOME_BONUS_SCOPE,
} from '../constants/enums';
import { generateZodEnumIds } from '../utils';
import { itemNameValidator, itemStringValidator, nullableNumberInput } from './helpers';
import {
  validateCasinoWageringFields,
  validateSportsbookWageringFields,
  wageringFieldsSchema,
} from './wageringConfigurationSchema';

const welcomeBonusCreateFormBaseSchema = z
  .object({
    name: itemNameValidator(1024),
    description: itemStringValidator({ label: 'Description', max: 4096, required: false }),
    product: z.enum(generateZodEnumIds(WELCOME_BONUS_PRODUCT_TYPE_OPTIONS)),
    eligibilityCriteria: z.enum(generateZodEnumIds(WELCOME_BONUS_ELIGIBILITY_OPTIONS)),
    validityPeriod: z.enum(generateZodEnumIds(WELCOME_BONUS_VALIDITY_PERIOD_OPTIONS)),
    status: z.enum(generateZodEnumIds(WELCOME_BONUS_STATUS_OPTIONS)),
    welcomeBonusScope: z.enum(generateZodEnumIds(WELCOME_BONUS_SCOPE_OPTIONS)).nullable(),
    affiliateIds: z.array(z.string()),
    bonusType: z.enum(generateZodEnumIds(WELCOME_BONUS_TYPE_OPTIONS)),
    rewardType: z.enum(generateZodEnumIds(WELCOME_BONUS_REWARD_TYPE_OPTIONS)),
    bonusCategory: z.enum(generateZodEnumIds(AFFILIATE_BONUS_CATEGORY_OPTIONS)),
    wageringMode: z.enum(generateZodEnumIds(WELCOME_BONUS_WAGERING_MODE_OPTIONS)),
    bonusPercentage: nullableNumberInput(),
    fixedAmount: nullableNumberInput(),
    capLimit: nullableNumberInput(),
    minDepositAmount: nullableNumberInput(),
    timeframe: z.coerce.number().min(0, 'Must be at least 0').max(1_000, 'Must be at most 1,000'),
    wageringRequirement: nullableNumberInput(),
    multiplier: nullableNumberInput(),
    incrementalUnlocksEnabled: z.boolean(),
    unlockBatchPercentage: nullableNumberInput(),
  })
  .extend(wageringFieldsSchema.shape);

export const welcomeBonusCreateFormSchema = welcomeBonusCreateFormBaseSchema.superRefine((data, ctx) => {
  const isPercentage = data.bonusCategory === AFFILIATE_BONUS_CATEGORY.PERCENTAGE;
  const isFixedAmountWagering = data.wageringMode === WELCOME_BONUS_WAGERING_MODE.FIXED_AMOUNT;
  const isSportsbook = data.product === AFFILIATE_CAMPAIGN_PRODUCT.SPORTSBOOK;
  const isCasino = data.product === AFFILIATE_CAMPAIGN_PRODUCT.CASINO;

  if (!data.welcomeBonusScope) {
    ctx.addIssue({
      path: ['welcomeBonusScope'],
      message: 'Scope is required.',
      code: z.ZodIssueCode.custom,
    });
  }

  if (data.welcomeBonusScope === WELCOME_BONUS_SCOPE.SPECIAL && data.affiliateIds.length === 0) {
    ctx.addIssue({
      path: ['affiliateIds'],
      message: 'At least one affiliate is required.',
      code: z.ZodIssueCode.custom,
    });
  }

  if (isPercentage) {
    if (data.bonusPercentage == null) {
      ctx.addIssue({
        path: ['bonusPercentage'],
        message: 'Bonus percentage is required.',
        code: z.ZodIssueCode.custom,
      });
    } else if (data.bonusPercentage < 0 || data.bonusPercentage > 1_000) {
      ctx.addIssue({
        path: ['bonusPercentage'],
        message: 'Must be between 0 and 1,000',
        code: z.ZodIssueCode.custom,
      });
    }
  } else if (data.fixedAmount == null) {
    ctx.addIssue({
      path: ['fixedAmount'],
      message: 'Fixed amount is required.',
      code: z.ZodIssueCode.custom,
    });
  } else if (data.fixedAmount < 0 || data.fixedAmount > 10_000_000) {
    ctx.addIssue({
      path: ['fixedAmount'],
      message: 'Must be between 0 and 10,000,000',
      code: z.ZodIssueCode.custom,
    });
  }

  if (isPercentage) {
    if (data.capLimit == null) {
      ctx.addIssue({
        path: ['capLimit'],
        message: 'Cap limit is required.',
        code: z.ZodIssueCode.custom,
      });
    } else if (data.capLimit < 0 || data.capLimit > 10_000_000) {
      ctx.addIssue({
        path: ['capLimit'],
        message: 'Must be between 0 and 10,000,000',
        code: z.ZodIssueCode.custom,
      });
    }
  }

  if (data.minDepositAmount == null) {
    ctx.addIssue({
      path: ['minDepositAmount'],
      message: 'Minimum deposit amount is required.',
      code: z.ZodIssueCode.custom,
    });
  } else if (data.minDepositAmount < 0 || data.minDepositAmount > 10_000_000) {
    ctx.addIssue({
      path: ['minDepositAmount'],
      message: 'Must be between 0 and 10,000,000',
      code: z.ZodIssueCode.custom,
    });
  }

  if (isFixedAmountWagering) {
    if (data.wageringRequirement == null) {
      ctx.addIssue({
        path: ['wageringRequirement'],
        message: 'Wagering requirement is required.',
        code: z.ZodIssueCode.custom,
      });
    } else if (data.wageringRequirement < 0 || data.wageringRequirement > 10_000_000) {
      ctx.addIssue({
        path: ['wageringRequirement'],
        message: 'Must be between 0 and 10,000,000',
        code: z.ZodIssueCode.custom,
      });
    }
  } else if (data.multiplier == null) {
    ctx.addIssue({
      path: ['multiplier'],
      message: 'Multiplier X is required.',
      code: z.ZodIssueCode.custom,
    });
  } else if (data.multiplier < 1 || data.multiplier > 1_000) {
    ctx.addIssue({
      path: ['multiplier'],
      message: 'Must be between 1 and 1,000',
      code: z.ZodIssueCode.custom,
    });
  }

  if (isSportsbook) {
    validateSportsbookWageringFields(data, ctx);
  }

  if (isCasino) {
    if (data.rewardType !== CAMPAIGN_REWARD_TYPE.REAL_MONEY) {
      ctx.addIssue({
        path: ['rewardType'],
        message: 'Casino welcome bonuses can only award Real Money.',
        code: z.ZodIssueCode.custom,
      });
    }

    validateCasinoWageringFields(data, ctx);

    if (data.incrementalUnlocksEnabled) {
      if (data.unlockBatchPercentage == null) {
        ctx.addIssue({
          path: ['unlockBatchPercentage'],
          message: 'Unlock batch percentage is required.',
          code: z.ZodIssueCode.custom,
        });
      } else if (data.unlockBatchPercentage < 1 || data.unlockBatchPercentage > 100) {
        ctx.addIssue({
          path: ['unlockBatchPercentage'],
          message: 'Must be between 1 and 100',
          code: z.ZodIssueCode.custom,
        });
      } else if (100 % data.unlockBatchPercentage !== 0) {
        ctx.addIssue({
          path: ['unlockBatchPercentage'],
          message: 'Must divide 100 exactly',
          code: z.ZodIssueCode.custom,
        });
      }
    }
  }
});

export type WelcomeBonusCreateFormData = z.infer<typeof welcomeBonusCreateFormSchema>;

// Immutable configuration stays outside edit state so incomplete legacy data cannot block campaign updates.
const welcomeBonusEditFormBaseSchema = z.object({
  name: itemNameValidator(1024),
  status: z.enum(generateZodEnumIds(WELCOME_BONUS_STATUS_OPTIONS)),
  description: itemStringValidator({ label: 'Description', max: 4096, required: false }),
  affiliateIds: z.array(z.string()),
});

export type WelcomeBonusEditFormData = z.infer<typeof welcomeBonusEditFormBaseSchema>;

export const createWelcomeBonusEditFormSchema = (
  currentStatus: WelcomeBonusStatus,
  welcomeBonusScope?: WelcomeBonusScope | null
) =>
  welcomeBonusEditFormBaseSchema.superRefine((data, ctx) => {
    if (data.status !== currentStatus && !isValidWelcomeBonusStatusTransition(currentStatus, data.status)) {
      ctx.addIssue({
        path: ['status'],
        message: `Invalid transition from ${currentStatus} to ${data.status}`,
        code: z.ZodIssueCode.custom,
      });
    }

    if (welcomeBonusScope === WELCOME_BONUS_SCOPE.SPECIAL && data.affiliateIds.length === 0) {
      ctx.addIssue({
        path: ['affiliateIds'],
        message: 'At least one affiliate is required.',
        code: z.ZodIssueCode.custom,
      });
    }
  });
